import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { Helmet, HelmetProvider } from "react-helmet-async";
import {
  selectCartItems,
  selectCartTotal,
  removeFromCart,
  updateQty,
} from "../redux/slice/CartSlice";
import { trackInitiateCheckout } from "../analytics/events";
import noimage from "../image/noimage.jpg";

export default function Cart() {
  const dispatch  = useDispatch();
  const navigate  = useNavigate();
  const items     = useSelector(selectCartItems);
  const subtotal  = useSelector(selectCartTotal);
  const shipping  = items.length > 0 && subtotal < 999 ? 99 : 0;
  const total     = subtotal + shipping;

  const handleCheckout = () => {
    trackInitiateCheckout(items, total);
    navigate("/checkout");
  };

  return (
    <>
      <HelmetProvider>
        <Helmet>
          <title>Your Cart | MeraGym Shop</title>
          <meta name="robots" content="noindex, nofollow" />
        </Helmet>
      </HelmetProvider>

      <section className="mg-cart-page">
        <div className="container">
          <nav className="pd-breadcrumb mb-3" aria-label="breadcrumb">
            <Link to="/shop">Shop</Link>
            <span>/</span>
            <span>Cart</span>
          </nav>
          <h1 className="mg-cart-page-title">
            <i className="fas fa-shopping-cart me-2"></i>Your Cart
            {items.length > 0 && (
              <span className="mg-cart-item-count">
                {items.reduce((s, i) => s + i.qty, 0)} item{items.reduce((s, i) => s + i.qty, 0) !== 1 ? "s" : ""}
              </span>
            )}
          </h1>

          {items.length === 0 ? (
            <div className="mg-empty-cart">
              <i className="fas fa-shopping-bag"></i>
              <h2>Your cart is empty</h2>
              <p>Looks like you haven't added anything yet.</p>
              <Link to="/shop" className="mg-cart-shop-btn">Start Shopping</Link>
            </div>
          ) : (
            <div className="row gy-4">
              {/* ── Cart Items ── */}
              <div className="col-lg-8">
                <div className="mg-cart-items-card">
                  {items.map((item) => (
                    <div className="mg-cart-item" key={item.key}>
                      <div className="mg-cart-img">
                        <img
                          src={item.image || noimage}
                          alt={item.title}
                          onError={(e) => { e.target.src = noimage; }}
                        />
                      </div>
                      <div className="mg-cart-meta">
                        <Link
                          to={`/shop/fitness-equipment/${item.slug}`}
                          className="mg-cart-item-title"
                        >
                          {item.title}
                        </Link>
                        {item.variantInfo && (
                          <span className="mg-cart-item-variant">{item.variantInfo}</span>
                        )}
                        <div className="mg-cart-price-row">
                          <span className="mg-cart-item-price">
                            ₹{item.price.toLocaleString("en-IN")}
                          </span>
                          {item.mrp > item.price && (
                            <span className="mg-cart-item-mrp">
                              ₹{item.mrp.toLocaleString("en-IN")}
                            </span>
                          )}
                        </div>
                        <div className="mg-cart-actions">
                          <div className="mg-qty-stepper">
                            <button
                              onClick={() => dispatch(updateQty({ key: item.key, qty: item.qty - 1 }))}
                              aria-label="Decrease"
                            >−</button>
                            <span>{item.qty}</span>
                            <button
                              onClick={() => dispatch(updateQty({ key: item.key, qty: item.qty + 1 }))}
                              aria-label="Increase"
                            >+</button>
                          </div>
                          <button
                            className="mg-cart-remove"
                            onClick={() => dispatch(removeFromCart(item.key))}
                          >
                            <i className="fas fa-trash-alt me-1"></i>Remove
                          </button>
                        </div>
                      </div>
                      <div className="mg-cart-line-total">
                        ₹{(item.price * item.qty).toLocaleString("en-IN")}
                      </div>
                    </div>
                  ))}
                </div>
                <Link to="/shop" className="mg-continue-shopping">
                  <i className="fas fa-arrow-left me-2"></i>Continue Shopping
                </Link>
              </div>

              {/* ── Order Summary ── */}
              <div className="col-lg-4">
                <div className="mg-order-summary">
                  <h3 className="mg-summary-title">Order Summary</h3>

                  <div className="mg-summary-rows">
                    <div className="mg-summary-row">
                      <span>Subtotal ({items.reduce((s, i) => s + i.qty, 0)} items)</span>
                      <span>₹{subtotal.toLocaleString("en-IN")}</span>
                    </div>
                    <div className="mg-summary-row">
                      <span>Shipping</span>
                      <span className={shipping === 0 ? "text-success fw-bold" : ""}>
                        {shipping === 0 ? "FREE" : `₹${shipping}`}
                      </span>
                    </div>
                    {shipping > 0 && (
                      <p className="mg-free-shipping-note">
                        <i className="bi bi-info-circle me-1"></i>
                        Add ₹{(999 - subtotal).toLocaleString("en-IN")} more for FREE shipping
                      </p>
                    )}
                  </div>

                  <div className="mg-summary-total">
                    <span>Total</span>
                    <span>₹{total.toLocaleString("en-IN")}</span>
                  </div>

                  <button className="mg-checkout-btn" onClick={handleCheckout}>
                    Proceed to Checkout
                    <i className="fas fa-arrow-right ms-2"></i>
                  </button>

                  <div className="mg-summary-trust">
                    <i className="fas fa-lock me-1"></i>
                    Secure & Encrypted Checkout
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </section>
    </>
  );
}
